import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  makeStyles,
  IconButton,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { getBackendUrl } from "../../config";
import {
  getContratoTemplate,
  createContratoTemplate,
  updateContratoTemplate,
  uploadContratoLogo,
} from "../../services/contratoService";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    borderBottom: "1px solid #e0e0e0",
  },
  content: {
    padding: "16px 24px",
    overflowY: "auto",
  },
  monoArea: {
    fontFamily: "monospace",
    fontSize: 13,
    width: "100%",
    minHeight: 300,
    padding: 8,
    border: "1px solid #ccc",
    borderRadius: 4,
    resize: "vertical",
    boxSizing: "border-box",
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  colorInput: {
    width: 48,
    height: 36,
    padding: 0,
    border: "none",
    cursor: "pointer",
    borderRadius: 4,
  },
  logoPreview: { maxHeight: 80, maxWidth: 200, marginTop: 8, display: "block" },
}));

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box mt={2}>{children}</Box>;
}

export default function ModeloModal({ open, onClose, templateId }) {
  const classes = useStyles();
  const isNew = !templateId;

  const [tab, setTab] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [cssStyle, setCssStyle] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1e40af");
  const [headerColor, setHeaderColor] = useState("#1e293b");
  const [logoUrl, setLogoUrl] = useState("");
  const [defaultClauses, setDefaultClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Garante spinner ANTES da primeira pintura ao abrir em modo edição
  useLayoutEffect(() => {
    if (open && !isNew) setLoading(true);
    if (!open) setLoading(false);
  }, [open, templateId]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setSnack({ open: false, message: "", severity: "success" });

    if (!isNew) {
      setLoading(true);
      getContratoTemplate(templateId)
        .then((t) => {
          setName(t.name || "");
          setDescription(t.description || "");
          setHtmlContent(t.htmlContent || "");
          setCssStyle(t.cssStyle || "");
          setPrimaryColor(t.primaryColor || "#1e40af");
          setHeaderColor(t.headerColor || "#1e293b");
          setLogoUrl(t.logoUrl || "");
          setDefaultClauses(Array.isArray(t.defaultClauses) ? t.defaultClauses : []);
        })
        .catch(() => setSnack({ open: true, message: "Erro ao carregar modelo.", severity: "error" }))
        .finally(() => setLoading(false));
    } else {
      setName("");
      setDescription("");
      setHtmlContent("");
      setCssStyle("");
      setPrimaryColor("#1e40af");
      setHeaderColor("#1e293b");
      setLogoUrl("");
      setDefaultClauses([]);
    }
  }, [open, templateId, isNew]);

  const handleSave = async () => {
    if (!name.trim()) {
      setSnack({ open: true, message: "O nome é obrigatório.", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = { name, description, htmlContent, cssStyle, primaryColor, headerColor, logoUrl, defaultClauses };
      if (isNew) {
        await createContratoTemplate(payload);
        setSnack({ open: true, message: "Modelo criado!", severity: "success" });
        setTimeout(() => onClose(true), 800);
      } else {
        await updateContratoTemplate(templateId, payload);
        setSnack({ open: true, message: "Modelo salvo!", severity: "success" });
        setTimeout(() => onClose(true), 800);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.error || "Erro ao salvar.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const addClause = () => {
    setDefaultClauses((prev) => [
      ...prev,
      { id: String(Date.now()), title: `Cláusula ${prev.length + 1}`, content: "" },
    ]);
  };
  const updateClause = (idx, field, value) => {
    setDefaultClauses((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const removeClause = (idx) => setDefaultClauses((prev) => prev.filter((_, i) => i !== idx));

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadContratoLogo(file);
      setLogoUrl(url);
    } catch {
      setSnack({ open: true, message: "Erro ao fazer upload do logo.", severity: "error" });
    }
  };

  const previewHtml = `<!DOCTYPE html><html><head><style>
    body { font-family: Arial, sans-serif; padding: 24px; max-width: 800px; margin: 0 auto; }
    h1 { color: ${primaryColor}; }
    .header { background: ${headerColor}; color: white; padding: 16px; border-radius: 4px; margin-bottom: 24px; }
    ${cssStyle}
  </style></head><body>
    <div class="header"><h2>Pré-visualização do Modelo</h2></div>
    ${htmlContent || "<p>Sem conteúdo HTML definido.</p>"}
    ${defaultClauses.map((c) => `<h3>${c.title}</h3><p>${c.content}</p>`).join("")}
  </body></html>`;

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
        <Box className={classes.dialogTitle}>
          <Typography variant="h6">
            {isNew ? "Novo Modelo de Contrato" : "Editar Modelo"}
          </Typography>
          <IconButton size="small" onClick={() => onClose(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent className={classes.content}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nome do Modelo"
                    fullWidth
                    required
                    value={name}
                    onChange={(e) => { const v = e.target.value; setName(v); }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Descrição"
                    fullWidth
                    value={description}
                    onChange={(e) => { const v = e.target.value; setDescription(v); }}
                  />
                </Grid>
              </Grid>

              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Conteúdo HTML" />
                <Tab label="CSS Personalizado" />
                <Tab label="Layout" />
                <Tab label="Cláusulas Padrão" />
              </Tabs>

              {/* HTML */}
              <TabPanel value={tab} index={0}>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Button size="small" variant="outlined" onClick={() => setPreviewOpen(true)}>
                    Pré-visualizar
                  </Button>
                </Box>
                <textarea
                  className={classes.monoArea}
                  value={htmlContent}
                  onChange={(e) => { const v = e.target.value; setHtmlContent(v); }}
                  placeholder="<p>Corpo do contrato...</p>"
                />
                <Typography variant="caption" color="textSecondary">
                  HTML livre. Cláusulas são configuradas na aba Cláusulas Padrão.
                </Typography>
              </TabPanel>

              {/* CSS */}
              <TabPanel value={tab} index={1}>
                <textarea
                  className={classes.monoArea}
                  value={cssStyle}
                  onChange={(e) => { const v = e.target.value; setCssStyle(v); }}
                  placeholder="/* CSS personalizado */"
                />
              </TabPanel>

              {/* Layout */}
              <TabPanel value={tab} index={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.colorRow}>
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={primaryColor}
                        onChange={(e) => { const v = e.target.value; setPrimaryColor(v); }}
                      />
                      <TextField
                        size="small"
                        label="Cor Primária"
                        value={primaryColor}
                        onChange={(e) => { const v = e.target.value; setPrimaryColor(v); }}
                        style={{ width: 160 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.colorRow}>
                      <input
                        type="color"
                        className={classes.colorInput}
                        value={headerColor}
                        onChange={(e) => { const v = e.target.value; setHeaderColor(v); }}
                      />
                      <TextField
                        size="small"
                        label="Cor do Cabeçalho"
                        value={headerColor}
                        onChange={(e) => { const v = e.target.value; setHeaderColor(v); }}
                        style={{ width: 160 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Logo</Typography>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} />
                    {logoUrl && (
                      <img
                        src={logoUrl.startsWith("/public") ? getBackendUrl() + logoUrl : logoUrl}
                        alt="Logo"
                        className={classes.logoPreview}
                      />
                    )}
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Cláusulas padrão */}
              <TabPanel value={tab} index={3}>
                <Button startIcon={<AddIcon />} onClick={addClause} variant="outlined" size="small">
                  Adicionar Cláusula Padrão
                </Button>
                <Box mt={2}>
                  {defaultClauses.map((cl, idx) => (
                    <Paper key={cl.id} variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
                      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                        <TextField
                          size="small"
                          label="Título"
                          value={cl.title}
                          onChange={(e) => { const v = e.target.value; updateClause(idx, "title", v); }}
                          style={{ flexGrow: 1 }}
                        />
                        <IconButton size="small" onClick={() => removeClause(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <textarea
                        className={classes.monoArea}
                        style={{ minHeight: 100, fontFamily: "inherit", fontSize: 14 }}
                        value={cl.content}
                        onChange={(e) => { const v = e.target.value; updateClause(idx, "content", v); }}
                        placeholder="Conteúdo da cláusula..."
                      />
                    </Paper>
                  ))}
                  {defaultClauses.length === 0 && (
                    <Typography color="textSecondary" variant="body2">
                      Nenhuma cláusula padrão. Serão copiadas ao criar um contrato com este modelo.
                    </Typography>
                  )}
                </Box>
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de preview */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pré-visualização do Modelo</DialogTitle>
        <DialogContent>
          <iframe
            title="preview"
            srcDoc={previewHtml}
            style={{ width: "100%", minHeight: 500, border: "1px solid #eee" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
